import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { User, Mail, Lock, AlertCircle, CheckCircle2, CreditCard, ExternalLink, Loader2, Bell } from 'lucide-react';
import { profileService } from '../services/profileService';
import { NotificationSettings } from '../components/notifications/NotificationSettings';

type ProfileTab = 'account' | 'notifications' | 'billing';

interface SubscriptionInfo {
  hasSubscription: boolean;
  stripeCustomerId?: string;
  stripeSubscriptionId?: string;
  status?: string;
  currentPeriodEnd?: number;
  cancelAtPeriodEnd?: boolean;
}

export const ProfilePage: React.FC = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<ProfileTab>('account');
  const [isChangingEmail, setIsChangingEmail] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [billingLoading, setBillingLoading] = useState(false);
  const [subscription, setSubscription] = useState<SubscriptionInfo | null>(null);
  const [subLoading, setSubLoading] = useState(false);

  const fetchSubscription = useCallback(async () => {
    setSubLoading(true);
    try {
      const data = await profileService.getSubscription();
      setSubscription(data);
    } catch {
      setSubscription({ hasSubscription: false });
    } finally {
      setSubLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'billing' && !subscription) {
      fetchSubscription();
    }
  }, [activeTab, subscription, fetchSubscription]);

  const handleEmailChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);
    try {
      await profileService.requestEmailChange(newEmail);
      setSuccess('Verification email sent to your new email address');
      setIsChangingEmail(false);
      setNewEmail('');
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to change email');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    setLoading(true);
    try {
      await profileService.requestPasswordChange(currentPassword);
      setSuccess('Password changed successfully');
      setIsChangingPassword(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  const handleManageSubscription = async () => {
    const customerId = subscription?.stripeCustomerId || user?.stripeCustomerId;
    if (!customerId) return;
    setBillingLoading(true);
    try {
      const { url } = await profileService.createBillingPortalSession(customerId);
      window.location.href = url;
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to open billing portal');
      setBillingLoading(false);
    }
  };

  const tabs: { id: ProfileTab; label: string; icon: React.ElementType }[] = [
    { id: 'account', label: 'Account', icon: User },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'billing', label: 'Subscription & Billing', icon: CreditCard },
  ];

  const statusLabel = (status?: string) => {
    switch (status) {
      case 'active': return { text: 'Active', color: 'bg-green-100 text-green-800' };
      case 'trialing': return { text: 'Trial', color: 'bg-blue-100 text-blue-800' };
      case 'past_due': return { text: 'Past Due', color: 'bg-red-100 text-red-800' };
      case 'canceled': return { text: 'Cancelled', color: 'bg-gray-100 text-gray-800' };
      default: return { text: 'Active', color: 'bg-green-100 text-green-800' };
    }
  };

  return (
    <div className="p-4 lg:p-8 max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Profile Settings</h1>
        <p className="text-gray-500 mt-1">Manage your account and subscription</p>
      </div>

      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      {success && (
        <Alert className="mb-4 border-green-200 bg-green-50">
          <CheckCircle2 className="h-4 w-4 text-green-600" />
          <AlertDescription className="text-green-800">{success}</AlertDescription>
        </Alert>
      )}

      {/* Tab Navigation */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="flex gap-6" aria-label="Profile tabs">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => { setActiveTab(tab.id); setError(''); setSuccess(''); }}
                className={`flex items-center gap-2 pb-3 px-1 border-b-2 text-sm font-medium transition-colors min-h-[44px] ${
                  activeTab === tab.id
                    ? 'border-green-600 text-green-700'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Icon className="h-4 w-4" />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Account Tab */}
      {activeTab === 'account' && (
        <div className="space-y-6">
          {/* Profile Info Card */}
          <Card className="bg-white rounded-xl border border-gray-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <User className="h-5 w-5 text-green-600" />
                Profile Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm text-gray-500">Name</Label>
                  <p className="text-gray-900 font-medium">{user?.profile?.firstName} {user?.profile?.lastName}</p>
                </div>
                <div>
                  <Label className="text-sm text-gray-500">Role</Label>
                  <p className="text-gray-900 font-medium capitalize">{user?.role?.toLowerCase().replace('_', ' ')}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Email Card */}
          <Card className="bg-white rounded-xl border border-gray-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Mail className="h-5 w-5 text-green-600" />
                Email Address
              </CardTitle>
            </CardHeader>
            <CardContent>
              {!isChangingEmail ? (
                <div className="flex items-center justify-between">
                  <p className="text-gray-900">{user?.email}</p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setIsChangingEmail(true)}
                    className="min-h-[44px]"
                  >
                    Change Email
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleEmailChange} className="space-y-4">
                  <div>
                    <Label htmlFor="newEmail">New Email Address</Label>
                    <Input
                      id="newEmail"
                      type="email"
                      value={newEmail}
                      onChange={(e) => setNewEmail(e.target.value)}
                      placeholder="Enter new email"
                      required
                      className="mt-1"
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button type="submit" disabled={loading} className="bg-green-600 hover:bg-green-700 text-white min-h-[44px]">
                      {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                      Send Verification
                    </Button>
                    <Button type="button" variant="outline" onClick={() => { setIsChangingEmail(false); setNewEmail(''); }} className="min-h-[44px]">
                      Cancel
                    </Button>
                  </div>
                </form>
              )}
            </CardContent>
          </Card>

          {/* Password Card */}
          <Card className="bg-white rounded-xl border border-gray-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Lock className="h-5 w-5 text-green-600" />
                Password
              </CardTitle>
            </CardHeader>
            <CardContent>
              {!isChangingPassword ? (
                <div className="flex items-center justify-between">
                  <p className="text-gray-500">••••••••</p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setIsChangingPassword(true)}
                    className="min-h-[44px]"
                  >
                    Change Password
                  </Button>
                </div>
              ) : (
                <form onSubmit={handlePasswordChange} className="space-y-4">
                  <div>
                    <Label htmlFor="currentPassword">Current Password</Label>
                    <Input
                      id="currentPassword"
                      type="password"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      required
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="newPassword">New Password</Label>
                    <Input
                      id="newPassword"
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      required
                      minLength={8}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="confirmPassword">Confirm New Password</Label>
                    <Input
                      id="confirmPassword"
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      minLength={8}
                      className="mt-1"
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button type="submit" disabled={loading} className="bg-green-600 hover:bg-green-700 text-white min-h-[44px]">
                      {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                      Update Password
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => { setIsChangingPassword(false); setCurrentPassword(''); setNewPassword(''); setConfirmPassword(''); }}
                      className="min-h-[44px]"
                    >
                      Cancel
                    </Button>
                  </div>
                </form>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Notifications Tab */}
      {activeTab === 'notifications' && (
        <NotificationSettings />
      )}

      {/* Billing Tab */}
      {activeTab === 'billing' && (
        <div className="space-y-6">
          {subLoading ? (
            <Card className="bg-white rounded-xl border border-gray-200">
              <CardContent className="flex items-center justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-green-600 mr-3" />
                <span className="text-gray-500">Loading subscription details...</span>
              </CardContent>
            </Card>
          ) : subscription?.hasSubscription ? (
            <>
              {/* Subscription Status Card */}
              <Card className="bg-white rounded-xl border border-gray-200">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <CreditCard className="h-5 w-5 text-green-600" />
                    Subscription
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Status</p>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusLabel(subscription.status).color}`}>
                        {statusLabel(subscription.status).text}
                      </span>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-gray-500">Current Period Ends</p>
                      <p className="text-gray-900 font-medium">
                        {subscription.currentPeriodEnd
                          ? new Date(subscription.currentPeriodEnd * 1000).toLocaleDateString('en-GB', {
                              day: 'numeric',
                              month: 'long',
                              year: 'numeric',
                            })
                          : '—'}
                      </p>
                    </div>
                  </div>
                  {subscription.cancelAtPeriodEnd && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                      <p className="text-sm text-amber-800">
                        Your subscription will cancel at the end of the current billing period.
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Manage Subscription Card */}
              <Card className="bg-white rounded-xl border border-gray-200">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <ExternalLink className="h-5 w-5 text-green-600" />
                    Manage Billing
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-500 text-sm mb-4">
                    View invoices, update payment method, or manage your subscription through the Stripe billing portal.
                  </p>
                  <Button
                    onClick={handleManageSubscription}
                    disabled={billingLoading}
                    className="bg-green-600 hover:bg-green-700 text-white min-h-[44px]"
                  >
                    {billingLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <ExternalLink className="h-4 w-4 mr-2" />}
                    Open Billing Portal
                  </Button>
                </CardContent>
              </Card>
            </>
          ) : (
            <Card className="bg-white rounded-xl border border-gray-200">
              <CardContent className="flex flex-col items-center justify-center py-12 text-center">
                <CreditCard className="h-10 w-10 text-gray-300 mb-3" />
                <p className="text-gray-900 font-medium">No active subscription</p>
                <p className="text-gray-500 text-sm mt-1">
                  You don't currently have an active subscription linked to your account.
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
};
